import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import Button from 'components/helpers/Button';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { TextWithOptionalLineBreaks } from 'components/helpers/Heading';
import HeadingTag from 'components/helpers/HeadingTag';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { classnames } from 'tailwindcss-classnames';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { getEnumValue } from 'lib/util/enum';
import { ButtonVariant } from 'components/helpers/Button/WithButton';

export type HomepageHeroNRCProps = Sitecore.Override.ComponentBase &
  Components.Heroes.HomepageHeroNrc.Fields.HomepageHeroNrc;

const HomepageHeroNRC = ({ fields }: HomepageHeroNRCProps): JSX.Element => {
  const isExperienceEditor = useExperienceEditor();
  const isFullBackground = getEnumValue(fields?.heroVariations) === `full`;
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  let lines;
  if (!fields?.numberOfLines) {
    lines = 4;
  } else {
    lines = parseInt(getEnumValue<string>(fields?.numberOfLines), 10);
  }
  const numberOfLines = classnames({
    'line-clamp-1': lines === 1,
    'line-clamp-2': lines === 2,
    'line-clamp-3': lines === 3,
    'line-clamp-4': lines === 4,
    'line-clamp-5': lines === 5,
  });
  const cta2TypeField = fields.cta2Type.value;
  const cta2Type: ButtonVariant =
    cta2TypeField === 'Default' || cta2TypeField.trim().length === 0
      ? 'secondary'
      : (cta2TypeField.toLowerCase() as ButtonVariant);

  // HACK: override image width to 1024px so it responds correctly
  const nextImageField = {
    ...fields.image,
    value: isFullBackground
      ? { ...fields.image.value }
      : {
          ...fields.image.value,
          height: `841px`,
        },
  };
  return (
    <>
      <div
        className={classnames('overflow-hidden')}
        data-component="authorable/Heroes/homepageheronrc"
      >
        <div
          className={classnames(
            'overflow-hidden',
            'relative',
            'max-w-screen-2xl',
            'w-full',
            'relative',
            'flex',
            'flex-col-reverse',
            'mx-auto',
            'mb-6',
            'lg:flex-row',
            'lg:flex-row-reverse',
            'lg:min-w-[730px]'
          )}
        >
          <div
            className={classnames(
              'flex',
              'flex-wrap',
              'justify-end',
              'h-auto',
              'w-full',
              'relative',
              'mt-2',
              'lg:mt-0',
              'lg:w-1/2',
              'pl-8',
              'lg:pl-0'
            )}
          >
            <ImageWrapper
              className={classnames(
                'z-10',
                'min-h-[250px]',
                'min-w-[250px]',
                'object-cover',
                'rounded-tl-[20rem]',
                'rounded-bl-[20rem]'
              )}
              field={nextImageField}
            />
            <>
              <span
                className={classnames(
                  'absolute',
                  'block',
                  'overflow-x-hidden',
                  'md:overflow-y-hidden',
                  'h-[300%]',
                  'md:h-[250%]',
                  'w-[200%]',
                  'md:w-[250%]',
                  'top-[82%]',
                  'md:top-[-74%]',
                  'left-[-50%]',
                  'md:left-[-240%]'
                )}
              />
            </>
            {isExperienceEditor || fields.imageCaption?.value ? (
              <div
                className={classnames(
                  'pt-4',
                  'px-5',
                  'md:pl-0',
                  'lg:w-2/3',
                  'text-sm',
                  'text-right',
                  'text-gray-darkGray'
                )}
              >
                <TextWithOptionalLineBreaks tag="" field={fields.imageCaption} />
              </div>
            ) : (
              <></>
            )}
          </div>
          <div className={classnames('w-full', 'z-10', 'lg:w-1/2')}>
            <div
              className={classnames(
                'flex',
                'flex-col',
                'h-full',
                'pt-3',
                'p-6',
                'md:px-11',
                'w-full',
                'justify-start',
                'xl:justify-center',
                'lg:pl-20',
                'lg:pr-10',
                'lg:bg-opacity-0'
              )}
            >
              <div>
                <HeadingTag headingLevel="h1" className={classnames('heading-xl', 'mb-4')}>
                  <TextWithOptionalLineBreaks tag="" field={fields.heroHeadline} />
                </HeadingTag>
                <RichTextA11yWrapper
                  className={classnames('md:text-body', `${numberOfLines}`)}
                  field={fields.description}
                />
              </div>
              {isExperienceEditor || fields.cta1.value.href || fields.cta2.value.href ? (
                <div
                  className={classnames(
                    'flex',
                    'flex-col',
                    'justify-between',
                    'pt-6',
                    'px-0',
                    'md:px-6',
                    'lg:px-0',
                    'w-full',
                    'lg:items-center',
                    'lg:justify-start',
                    'lg:flex-row'
                  )}
                >
                  {fields?.useLinkButton.value ? (
                    <>
                      <LinkA11yWrapper
                        className={classnames('underline', 'p-2.5', 'text-white')}
                        editable={false}
                        field={fields.cta1}
                      />
                      <LinkA11yWrapper
                        className={classnames('underline', 'p-2.5', 'text-white')}
                        editable={false}
                        field={fields.cta2}
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        linkField={fields.cta1}
                        className={classnames('text-center', 'lg:mr-6')}
                      />
                      <Button
                        linkField={fields.cta2}
                        className={classnames('text-center', 'mt-4', 'md:mb-1', 'lg:my-0')}
                        variant={cta2Type}
                      />
                    </>
                  )}
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomepageHeroNRC;
